import type {ApolloError} from '@apollo/client';
import {useCallback, useState} from 'react';

import {
  DismissedBannerDocument,
  useDismissedBannerQuery,
  useSetDismissedBannerMutation,
} from '../../generated/graphql';
import {viewingAs} from '../../util/admin';
import {doNotRetryContext, propagateErrorsContext} from '../../util/errors';

// DISMISSAL_CONTEXT keeps both operations off the global error surface. Which
// banners a viewer dismissed is decorative, so a failure shouldn't blank the
// app, claim it is offline, or retry multiple times.
const DISMISSAL_CONTEXT = {
  ...propagateErrorsContext(),
  ...doNotRetryContext(),
};

type BannerDismissal = {
  dismiss: (() => void) | null;
  dismissed: boolean;
  error: ApolloError | undefined;
  loading: boolean;
};

/**
 * useBannerDismissal reports whether a server-persisted banner is dismissed for
 * the current viewer and exposes an action to dismiss it.
 *
 * Dismissal is keyed by `bannerId`; a null id means no banner, so `dismiss` is
 * null. Pass `resetAt` (epoch ms, e.g. a period boundary) to re-show the banner
 * once that boundary passes, or omit it for a permanent dismissal.
 *
 * While viewing as another user, `dismiss` only hides the banner for this
 * session so the dismissal isn't attributed to them. Pass
 * `persistWhileImpersonating` to write it through anyway.
 */
export function useBannerDismissal(
  bannerId: string | null,
  {
    resetAt,
    persistWhileImpersonating = false,
  }: {resetAt?: number | null; persistWhileImpersonating?: boolean} = {}
): BannerDismissal {
  const {data, loading, error} = useDismissedBannerQuery({
    variables: {id: bannerId ?? ''},
    skip: bannerId == null,
    context: DISMISSAL_CONTEXT,
  });
  const [setDismissedBanner] = useSetDismissedBannerMutation();
  const [dismissedThisSession, setDismissedThisSession] = useState(false);

  const dismissedAt = data?.dismissedBanner?.dismissedAt;
  // Treat loading as dismissed so the banner never flashes before its
  // persisted state resolves.
  const dismissed =
    dismissedThisSession ||
    loading ||
    (dismissedAt != null &&
      (resetAt == null ||
        // A reset boundary in the future hasn't elapsed, so it can't have
        // invalidated this dismissal yet; honor it until the boundary passes.
        // oxlint-disable-next-line react/purity
        resetAt > Date.now() ||
        new Date(dismissedAt).getTime() >= resetAt));

  const dismissBanner = useCallback(() => {
    if (bannerId == null) {
      return;
    }

    if (!persistWhileImpersonating && viewingAs() !== '') {
      setDismissedThisSession(true);
      return;
    }

    void setDismissedBanner({
      variables: {id: bannerId},
      context: DISMISSAL_CONTEXT,
      // Write the dismissal into the cache up front so the banner hides
      // instantly; Apollo reverts it if the mutation fails.
      optimisticResponse: {
        __typename: 'Mutation',
        setDismissedBanner: {
          __typename: 'SetDismissedBannerPayload',
          success: true,
        },
      },
      update: cache => {
        cache.writeQuery({
          query: DismissedBannerDocument,
          variables: {id: bannerId},
          data: {
            __typename: 'Query',
            dismissedBanner: {
              __typename: 'DismissedBanner',
              id: bannerId,
              dismissedAt: new Date().toISOString(),
            },
          },
        });
      },
    }).catch(() => {
      // The optimistic write already reverted; nothing else to do.
    });
  }, [bannerId, persistWhileImpersonating, setDismissedBanner]);

  // No banner means nothing to dismiss, so there is no action to expose.
  const dismiss = bannerId == null ? null : dismissBanner;

  return {dismiss, dismissed, error, loading};
}
